import { OptimizationInfo } from './../../../wizard/models/optimization-info.model';
import { Component, EventEmitter, Input, OnInit, Output, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { DialogHandlerService } from '@core/services/dialog-handler.service';
import { JobFileService } from '@core/services/job-file.service';
import { USE_PAGINATED_VEHICLE_SELECTOR } from '@configs/constants';
import { EntityMap } from '@shared/models/types';
import { DropDownValue } from 'app/modules/dynamic-form/models/drop-down-value';
import { Property } from 'app/modules/dynamic-form/models/property.model';
import { AddExternalVehicleCategory } from 'app/modules/wizard/components/add-external-vehicle-length-category/add-external-vehicle-length-category.component';
import { first, tap, switchMap, take, takeUntil, distinctUntilChanged, debounceTime } from 'rxjs/operators';
import { VehicleCountModifierComponent, VehicleType, VehicleCategoryResponse } from '../../../wizard/components/vehicle-count-modifier/vehicle-count-modifier.component';
import { DynamicFormService } from '../../services/dynamic-form.service';
import { Actions, Select, Store } from '@ngxs/store';
import { Subject } from 'rxjs';
import { setOptimizationInfo, ResetOptimizationInfo, LoadJobFileAction } from 'app/modules/wizard/store/wizard.actions';
import { WizardState } from 'app/modules/wizard/store/wizard.state';
import { Observable } from 'rxjs';
import { EditDigitalTwinComponent } from 'app/modules/wizard/components/edit-digital-twin/edit-digital-twin.component';
import { AlertDialogType } from '@shared/models';
import { CapacityWarningDialogComponent } from '@shared/components/capacity-warning-dialog/capacity-warning-dialog.component';
import { ActivatedRoute } from '@angular/router';
import { DialogHandlerDefaultDialogCustomPositionChoice } from '@core/models/enums/dialog-custom-position-choice.enum';

export interface VehicleCategory {
  vehicleCategory: string;
  vehiclesData: VehicleData[];
}

export interface VehiclesWithOrgType {
  mainOrgVehicles: VehicleCategory[];
  orgPatrnerVehicles: VehicleCategory[];
}
export interface VehicleData {
  id: string;
  maxLoadVolume: number;
  maxLoadWeight: number;
  dimensions: {
    length: number;
    width: number;
    height: number;
  };
  vehicleNumber: string | number;
  costPerKM: number;
  capacityUtilization?: number;
  isSelected?: boolean;
  isUpdated?: boolean;
}

export interface StandardVehicle {
  vehicleType: string;
  count: number;
  maxLoadVolume: number;
  maxLoadWeight: number;
  costPerKM: number;
  capacityUtilization: number;
}

@Component({
    selector: 'hmt-vehicle-capacity',
    templateUrl: './vehicle-capacity.component.html',
    styleUrls: ['./vehicle-capacity.component.scss'],
    standalone: false
})
export class VehicleCapacityComponent implements OnInit, OnDestroy, OnChanges {

  @Input() internalVehicleCategories: EntityMap<
    number,
    { count: number; vehicleData: VehicleData[] }
  > = {};
  @Input() externalVehicleCategories: EntityMap<
    number,
    { count: number; vehicleData: VehicleData[] }
  > = {};
  @Input() isOptimizing = false;

  @Select(WizardState.getJobFile) jobFile$: Observable<any>;
  @Select(WizardState.getOptimizationInfo)
  optimizationInfo$: Observable<OptimizationInfo>;

  private unsubscribe: Subject<void> = new Subject();
  private isCapacityWarningDialogOpen = false;
  /**
   * Global-utilization slider changes in paginated mode. The slider fires on every `input`, so the
   * impact check / prompt / jobfile write is debounced until a drag settles into one value.
   */
  private globalUtilInput$ = new Subject<number>();
  /** What the user last confirmed, so dismissing the overwrite prompt can put the slider back. */
  private lastCommittedGlobalUtilization = 100;
  standardVehicleCategories: EntityMap<
    number,
    { count: number; vehicleData: VehicleData[] }
  > = {};
  globalCapacityUtilization = 100;
  config: Property;
  group: FormGroup;
  values: DropDownValue[] = [];
  paramsMap = {}; // TODO: implement a type
  queryParamsMap = {};
  dataMap: any;
  jobFileData: any = {};
  private currentJobRefId: string | null = null;
  availableInternalVehicleCategories: EntityMap<
    number,
    { count: number; vehicleData: VehicleData[] }
  > = {};
  availableExternalVehicleCategories: EntityMap<
    number,
    { count: number; vehicleData: VehicleData[] }
  > = {};
  availableStandardVehicleCategories: EntityMap<
    number,
    { count: number; vehicleData: VehicleData[] }
  > = {};
  vehicleCategoryListInternal: EntityMap<
    number,
    { count: number; vehicleData: VehicleData[] }
  > = {};
  vehicleCategoryListExternal: EntityMap<
    number,
    { count: number; vehicleData: VehicleData[] }
  > = {};
  vehicleCategoryListStandard: EntityMap<
    number,
    { count: number; vehicleData: VehicleData[] }
  > = {};
  standardVehicles: StandardVehicle[] = [];
  standardVehicleForm = new FormGroup({
    vehicleType: new FormControl('', Validators.required),
    count: new FormControl(1, [Validators.required, Validators.min(1)]),
    maxLoadVolume: new FormControl(15, [Validators.required, Validators.min(0)]),
    maxLoadWeight: new FormControl(15000, [Validators.required, Validators.min(0)]),
    costPerKM: new FormControl(0, [Validators.required, Validators.min(0)]),
    capacityUtilization: new FormControl(100, [Validators.required, Validators.min(0), Validators.max(100)])
  });

  constructor(
    private readonly dialogHandlerService: DialogHandlerService,
    private dynamicFormService: DynamicFormService,
    public store$: Store,
    public actions$: Actions,
    private activatedRoute: ActivatedRoute,
    private readonly jobFileService: JobFileService
  ) {}

  /**
   * Write the current vehicle capacities straight to the jobfile. Called after the Edit Digital Twin
   * dialog's Update so the change is persisted immediately (saveToDatabase only updates the NGXS
   * store, which previously meant the jobfile was written only at optimize time).
   */
  /** categoryName -> categoryId, from the summary; lets the popup filter by the real category id. */
  private categoryIdByName = new Map<string, string>();

  /**
   * Build the internal/external category rows from the jobfile's per-ownership category counts.
   * `vehicleData` is intentionally left EMPTY: nothing on this screen renders individual vehicles (the
   * popup pages them from the jobfile), and the optimize request no longer carries internal/external
   * vehicles — the jobfile is authoritative. Empty arrays keep the existing `.map`/`.slice` call sites
   * safe (only `undefined` would throw).
   */
  private loadCategoryShellsFromJobFile(): void {
    const orgId = this.jobFileData?.orgId;
    const jobRefId = this.jobFileData?.jobRefId;
    if (!orgId || !jobRefId) { return; }

    this.jobFileService.getVehicleSelectionCategories(orgId, jobRefId)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: (res: any) => this.applyCategoryShells(res),
        error: (err) => console.error('Failed to load vehicle categories from jobfile', err)
      });
  }

  /**
   * Turn one ownership bucket of `/vehicle-selection/categories`
   * ({categoryName: {total, selected, categoryId}}) into the shape this screen renders:
   * `count` = SELECTED (what the cards show), `totalCount` = all rows in the category.
   */
  private toCategoryShells(bucket: any): EntityMap<number, { count: number; vehicleData: VehicleData[] }> {
    const out: any = {};
    Object.keys(bucket || {}).forEach(name => {
      const info = bucket[name] || {};
      const total = Number(info.total || 0);
      const selected = info.selected != null ? Number(info.selected) : total;
      out[name] = { count: selected, totalCount: total, vehicleData: [] };
      if (info.categoryId) { this.categoryIdByName.set(String(name), String(info.categoryId)); }
    });
    return out;
  }

  /**
   * Replace the internal/external category rows with fresh, jobfile-backed shells and mirror them into
   * the NGXS optimization info. `vehicleData` is deliberately reset to `[]` so the counts the template
   * shows come from the server's `selected` — keeping stale in-memory rows here is what made an edit
   * look like it had no effect (`getSelectedVehicleCount` prefers the row list whenever one exists).
   */
  private applyCategoryShells(res: any): void {
    this.internalVehicleCategories = this.toCategoryShells(res?.internal);
    this.externalVehicleCategories = this.toCategoryShells(res?.external);
    // Keep the same derived structures the legacy path produced, so downstream code is unchanged.
    this.vehicleCategoryListInternal = this.createDeepCopy(this.internalVehicleCategories);
    this.vehicleCategoryListExternal = this.createDeepCopy(this.externalVehicleCategories);

    this.generateVehicleCategoryFormControls(this.internalVehicleCategories);
    this.saveToDatabase();
  }

  /**
   * Ask the backend for the category summary, which also SEEDS the jobfile with the real vehicle data
   * on first call (no-op afterwards — it never overwrites saved edits). Also caches name -> id so the
   * selector popup can filter by category id rather than v1's local key.
   */
  private seedVehicleSelectionIfNeeded(): void {
    if (!USE_PAGINATED_VEHICLE_SELECTOR) { return; }
    const orgId = this.jobFileData?.orgId;
    const jobRefId = this.jobFileData?.jobRefId;
    if (!orgId || !jobRefId) { return; }
    this.jobFileService.initVehicleSelection(orgId, jobRefId, this.jobFileData?.operationId || '')
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: (summaries: any[]) => {
          (summaries || []).forEach(s => {
            if (s?.categoryName && s?.categoryId) {
              this.categoryIdByName.set(String(s.categoryName), String(s.categoryId));
            }
          });
        },
        error: (err) => console.error('Failed to seed vehicle selection', err)
      });
  }

  /**
   * Refresh the category rows/counts from the jobfile-backed summary (no fleet load). Used after the
   * server-paged dialog saved its changes directly to the jobfile.
   *
   * Reads `/vehicle-selection/categories` — the ONLY endpoint that reports a per-category SELECTED
   * count split by ownership. `/vehicle-selection/init` (used here before) returns
   * VehicleCategorySummaryDTO, which carries `totalCount` and nothing else, so deselecting vehicles
   * could never change the number on screen. The rows are also replaced wholesale (see
   * applyCategoryShells) instead of patching `count` on the stale objects, because the template's
   * getSelectedVehicleCount() ignores `count` whenever in-memory `vehicleData` is present.
   */
  private refreshCategoryCountsFromJobFile(): void {
    const orgId = this.jobFileData?.orgId;
    const jobRefId = this.jobFileData?.jobRefId;
    if (!orgId || !jobRefId) { return; }
    this.jobFileService.getVehicleSelectionCategories(orgId, jobRefId)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: (res: any) => {
          this.applyCategoryShells(res);
          // The wizard store still holds the pre-edit jobfile, and processAlreadySavedData() rebuilds
          // the categories from jobFile.vehicleCapacities — without this reload the next jobFile$
          // emission would restore the old selection over the freshly refreshed counts.
          this.store$.dispatch(new LoadJobFileAction(jobRefId));
        },
        error: (err) => console.error('Failed to refresh vehicle category counts', err)
      });
  }

  /**
   * Write the standard vehicles straight to the jobfile. They are built entirely in the browser, so
   * without this they existed only in NGXS until the optimize call — which meant the backend's
   * utilization-impact check could not see their individually-set utilization and the global slider
   * overwrote them without asking. Only the standard bucket is patched; internal/external stay as they
   * are (this screen holds no rows for them in paginated mode).
   */
  private persistStandardVehicleCapacitiesToJobFile(): void {
    const orgId = this.jobFileData?.orgId;
    const jobRefId = this.jobFileData?.jobRefId;
    if (!orgId || !jobRefId) {
      return;
    }
    const standard: any = {};
    Object.keys(this.standardVehicleCategories || {}).forEach(key => {
      const cat = this.standardVehicleCategories[key] || ({} as any);
      const vehiclesData = (cat as any).vehicleData || (cat as any).vehiclesData || [];
      standard[key] = {
        count: cat.count != null ? cat.count : vehiclesData.length,
        lengthType: (cat as any).lengthType,
        repeatable: (cat as any).repeatable,
        vehiclesData
      };
    });
    this.jobFileService.saveStandardVehicleCapacities(orgId, jobRefId, standard)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        error: (err) => console.error('Failed to save standard vehicles to jobfile', err)
      });
  }

  private persistVehicleCapacitiesToJobFile(): void {
    const orgId = this.jobFileData?.orgId;
    const jobRefId = this.jobFileData?.jobRefId;
    if (!orgId || !jobRefId) {
      return;
    }
    const toBucket = (categories: any) => {
      const bucket: any = {};
      Object.keys(categories || {}).forEach(key => {
        const cat = categories[key] || {};
        const vehiclesData = (cat.vehicleData || cat.vehiclesData || []);
        bucket[key] = {
          count: cat.count != null ? cat.count : vehiclesData.length,
          lengthType: cat.lengthType,
          repeatable: cat.repeatable,
          vehiclesData: vehiclesData
        };
      });
      return bucket;
    };
    const payload = {
      internal: toBucket(this.internalVehicleCategories),
      external: toBucket(this.externalVehicleCategories),
      standard: toBucket(this.standardVehicleCategories)
    };
    this.jobFileService.saveVehicleCapacities(orgId, jobRefId, payload).subscribe({
      error: (err) => console.error('Failed to save vehicle capacities to jobfile', err)
    });
  }

  /**
   * Paginated mode: the browser holds no internal/external vehicle rows, so anything that has to reach
   * those vehicles must go through the jobfile.
   */
  private get usePaginatedSelector(): boolean {
    return USE_PAGINATED_VEHICLE_SELECTOR && !!this.jobFileData?.orgId && !!this.jobFileData?.jobRefId;
  }

  ngOnInit(): void {
    // Check if isOptimizing is provided in dataMap (for dynamic-form usage)
    if (this.dataMap && this.dataMap['isOptimizing'] !== undefined) {
      this.isOptimizing = this.dataMap['isOptimizing'];
    }
    this.addFormControls();

    // Debounced global-utilization pipeline (paginated mode only; see updateGlobalCapacityUtilization).
    // Deliberately NOT distinctUntilChanged: dismissing the overwrite prompt reverts the slider without
    // committing, and the user must be able to re-apply the same value afterwards.
    this.globalUtilInput$
      .pipe(debounceTime(400), takeUntil(this.unsubscribe))
      .subscribe(value => this.decideGlobalUtilizationApplyMode(value));
    // Update form disabled state after form controls are added
    if (this.group) {
      this.updateFormDisabledState();
    }

    this.jobFile$.pipe(
      takeUntil(this.unsubscribe),
      tap((data) => {
        this.jobFileData = {
          ...this.jobFileData,
          ...(data || {})
        };
      }),
      switchMap((jobFile) => this.optimizationInfo$.pipe(
        first(),
        tap((info) => {
          if (info && jobFile?.jobRefId) {
            const optimizationBelongsToDifferentJob = info.jobRefId && info.jobRefId !== jobFile.jobRefId;
            const isNewJobWithoutOptimization = !info.jobRefId && jobFile.jobRefId;

            if (optimizationBelongsToDifferentJob || isNewJobWithoutOptimization) {
              this.store$.dispatch(new ResetOptimizationInfo());
              return null;
            }
          }
          return info;
        })
      ))
    ).subscribe((info) => {
      if (info !== null && info && (
        Object.keys(info.internalVehicleCategories || {}).length > 0 ||
        Object.keys(info.externalVehicleCategories || {}).length > 0 ||
        Object.keys(info.standardVehicleCategories || {}).length > 0
      )) {
        const hasIndividualUtilization = info.isIndividualUtilizationSet === true;
        const hasUpdatedVehicles = this.checkForUpdatedVehicles(info);

        this.loadFromOptimizationInfo(info);

        if (!hasIndividualUtilization && !hasUpdatedVehicles) {
          this.processAlreadySavedData();
        }
      } else {
        if (this.jobFileData && this.jobFileData.operationId) {
          this.loadAvailableVehicles(this.jobFileData.operationId);
          // Seed the jobfile with the real vehicle data (once) so the paged, jobfile-backed selector
          // popup has rows to serve. Without this a v1 job that never optimized has no
          // vehicleCapacities, and .../vehicle-selection/page returns an empty page.
          this.seedVehicleSelectionIfNeeded();
        } else {
          this.loadAvailableVehicles();
        }
      }
    });

    if (!this.standardVehicleCategories || Object.keys(this.standardVehicleCategories).length === 0) {
      this.standardVehicleCategories = {};
      this.vehicleCategoryListStandard = {};

      this.store$.dispatch(
        new setOptimizationInfo({
          internalVehicleCategories: this.internalVehicleCategories || {},
          externalVehicleCategories: this.externalVehicleCategories || {},
          standardVehicleCategories: this.standardVehicleCategories,
          vehicleCategoryListInternal: this.vehicleCategoryListInternal || {},
          vehicleCategoryListExternal: this.vehicleCategoryListExternal || {},
          vehicleCategoryListStandard: this.vehicleCategoryListStandard,
          globalCapacityUtilization: this.globalCapacityUtilization ?? 100
        })
      );
    }
  }

  private loadFromOptimizationInfo(info: OptimizationInfo): void {
    this.internalVehicleCategories = {
      ...info.internalVehicleCategories,
    };
    this.externalVehicleCategories = {
      ...info.externalVehicleCategories,
    };

    // For standard vehicles, ensure isSelected is always true
    this.standardVehicleCategories = {};
    for (const category in info.standardVehicleCategories) {
      if (info.standardVehicleCategories.hasOwnProperty(category)) {
        this.standardVehicleCategories[category] = {
          ...info.standardVehicleCategories[category],
          vehicleData: info.standardVehicleCategories[category].vehicleData.map(vehicle => ({
            ...vehicle,
            isSelected: true // Always true for standard vehicles
          }))
        };
      }
    }

    this.vehicleCategoryListInternal = {
      ...info.vehicleCategoryListInternal,
    };
    this.vehicleCategoryListExternal = {
      ...info.vehicleCategoryListExternal,
    };
    this.vehicleCategoryListStandard = {
      ...info.vehicleCategoryListStandard,
    };
    this.standardVehicles = (info as any).standardVehicles || [];
    this.globalCapacityUtilization = info.globalCapacityUtilization ?? 100;
    // Keep the revert baseline on the loaded value, so dismissing the overwrite prompt restores what
    // was actually in force rather than a stale default.
    this.lastCommittedGlobalUtilization = this.globalCapacityUtilization;
    this.group.get('globalCapacityUtilization').setValue(this.globalCapacityUtilization);
    this.generateVehicleCategoryFormControls(
      {
        ...this.internalVehicleCategories,
        ...this.externalVehicleCategories,
        ...this.standardVehicleCategories
      }
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOptimizing'] && this.group) {
      this.updateFormDisabledState();
    }
    // Check if dataMap changed and contains isOptimizing
    if (changes['dataMap'] && this.dataMap && this.dataMap['isOptimizing'] !== undefined) {
      this.isOptimizing = this.dataMap['isOptimizing'];
      if (this.group) {
        this.updateFormDisabledState();
      }
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  private updateFormDisabledState(): void {
    if (!this.group) {
      return;
    }

    const controls = ['useSelectedVehiclesForAll', 'useVehicleCosts', 'globalCapacityUtilization'];
    controls.forEach(controlName => {
      const control = this.group.get(controlName);
      if (control) {
        if (this.isOptimizing) {
          if (!control.disabled) {
            control.disable({ emitEvent: false });
          }
        } else {
          if (control.disabled) {
            control.enable({ emitEvent: false });
          }
        }
      }
    });
  }

  addFormControls(): void {
    // Only add controls if they don't exist
    if (!this.group.get('useSelectedVehiclesForAll')) {
      this.group.addControl('useSelectedVehiclesForAll', new FormControl({ value: false, disabled: this.isOptimizing }));
    }
    if (!this.group.get('useVehicleCosts')) {
      this.group.addControl('useVehicleCosts', new FormControl({ value: true, disabled: this.isOptimizing }));
    }
    if (!this.group.get('globalCapacityUtilization')) {
      this.group.addControl('globalCapacityUtilization', new FormControl({ value: 100, disabled: this.isOptimizing }));
    }
    this.group.addControl('vehicleCapacities', new FormGroup({
      internal: new FormGroup({}),
      external: new FormGroup({}),
      standard: new FormGroup({}),
    }));
  }

  private createDeepCopy<T>(object: T): T {
    return JSON.parse(JSON.stringify(object || {}));
  }

  public processAlreadySavedData(): void {
    if (!this.jobFileData.vehicleCapacities) {
      return;
    }

    const internal = [];
    const external = [];
    const standard = [];

    const vehicleCapacities = this.jobFileData.vehicleCapacities;
    const useSelectedVehiclesForAll: boolean =
      this.jobFileData.useSelectedVehiclesForAll ?? false;
    const useVehicleCosts: boolean =
      this.jobFileData.useVehicleCosts ?? true;

    this.availableInternalVehicleCategories = this.createDeepCopy(this.internalVehicleCategories);
    this.internalVehicleCategories = this.createDeepCopy(this.internalVehicleCategories);

    this.availableExternalVehicleCategories = this.createDeepCopy(this.externalVehicleCategories || {});
    this.externalVehicleCategories = this.createDeepCopy(this.externalVehicleCategories || {});

    this.availableStandardVehicleCategories = this.createDeepCopy(this.standardVehicleCategories || {});
    this.standardVehicleCategories = this.createDeepCopy(this.standardVehicleCategories || {});

    let isIndividualUtilizationSet = false;
    let foundNonUpdatedCapacityUtilization = false;
    let globalCapacityValue = 100;

    if (vehicleCapacities.internal) {
      Object.keys(vehicleCapacities.internal).forEach((k) => {
        internal.push({ [k]: vehicleCapacities.internal[k] });

        this.internalVehicleCategories = {
          ...this.internalVehicleCategories,
          [k]: {
            count: vehicleCapacities.internal[k]?.count ?? 0,
            vehicleData: [...(vehicleCapacities.internal[k]?.vehiclesData ?? [])].map(vehicle => {
              if (vehicle.isUpdated === true) {
                isIndividualUtilizationSet = true;
              }
              if ((vehicle.isUpdated === false || vehicle.isUpdated === undefined) && !foundNonUpdatedCapacityUtilization &&
                  vehicle.capacityUtilization !== undefined) {
                globalCapacityValue = vehicle.capacityUtilization;
                foundNonUpdatedCapacityUtilization = true;
              }
              return {
                ...vehicle,
                isUpdated: vehicle.isUpdated || false,
                isSelected: vehicle.isSelected !== undefined ? vehicle.isSelected : true
              };
            }),
          }
        };
      });
    }

    if (vehicleCapacities.external) {
      Object.keys(vehicleCapacities.external).forEach((k) => {
        external.push({ [k]: vehicleCapacities.external[k] });

        this.externalVehicleCategories = {
          ...this.externalVehicleCategories,
          [k]: {
            count: vehicleCapacities.external[k]?.count ?? 0,
            vehicleData: [...(vehicleCapacities.external[k]?.vehiclesData ?? [])].map(vehicle => {
              if (vehicle.isUpdated === true) {
                isIndividualUtilizationSet = true;
              }
              if ((vehicle.isUpdated === false || vehicle.isUpdated === undefined) && !foundNonUpdatedCapacityUtilization &&
                  vehicle.capacityUtilization !== undefined) {
                globalCapacityValue = vehicle.capacityUtilization;
                foundNonUpdatedCapacityUtilization = true;
              }
              return {
                ...vehicle,
                isUpdated: vehicle.isUpdated || false,
                isSelected: vehicle.isSelected !== undefined ? vehicle.isSelected : true
              };
            }),
          }
        };
      });
    }

    if (vehicleCapacities.standard) {
      Object.keys(vehicleCapacities.standard).forEach((k) => {
        standard.push({ [k]: vehicleCapacities.standard[k] });

        this.standardVehicleCategories = {
          ...this.standardVehicleCategories,
          [k]: {
            count: vehicleCapacities.standard[k]?.count ?? 0,
            vehicleData: [...(vehicleCapacities.standard[k]?.vehiclesData ?? [])].map(vehicle => {
              if (vehicle.isUpdated === true) {
                isIndividualUtilizationSet = true;
              }
              if ((vehicle.isUpdated === false || vehicle.isUpdated === undefined) && !foundNonUpdatedCapacityUtilization &&
                  vehicle.capacityUtilization !== undefined) {
                globalCapacityValue = vehicle.capacityUtilization;
                foundNonUpdatedCapacityUtilization = true;
              }
              return {
                ...vehicle,
                isUpdated: vehicle.isUpdated || false,
                isSelected: true // Always true for standard vehicles
              };
            }),
          }
        };
      });
    }

    // The jobfile records the global the user actually set; only fall back to inferring it from the
    // first non-individually-edited vehicle (below) for jobs saved before that field existed. The
    // inference cannot work in paginated mode anyway — internal/external carry no rows here, so once
    // every standard vehicle is individually edited it found nothing and snapped the slider to 100.
    const savedGlobal = this.jobFileData?.vehicleSelectionIntent?.globalUtilization;
    if (savedGlobal != null) {
      globalCapacityValue = Number(savedGlobal);
    }

    this.globalCapacityUtilization = globalCapacityValue;
    this.lastCommittedGlobalUtilization = globalCapacityValue;

    if (this.group && this.group.get('globalCapacityUtilization')) {
      this.group.get('globalCapacityUtilization').setValue(this.globalCapacityUtilization);
    }

    this.generateVehicleCategoryFormControls({
      ...this.internalVehicleCategories,
      ...this.externalVehicleCategories,
      ...this.standardVehicleCategories
    });

    this.group.get('vehicleCapacities').patchValue({
      internal,
      external,
      standard
    });
    this.group
      .get('useSelectedVehiclesForAll')
      .patchValue(useSelectedVehiclesForAll);
    this.group.get('useVehicleCosts').patchValue(useVehicleCosts);

    this.saveToDatabase(isIndividualUtilizationSet);
  }

  public loadAvailableVehicles(id?: string): void {
    if (USE_PAGINATED_VEHICLE_SELECTOR && this.jobFileData?.orgId && this.jobFileData?.jobRefId) {
      // Paginated mode: build the category rows from the JOBFILE's per-ownership category counts and
      // hold NO vehicle data (the popup pages rows from the jobfile on demand). This replaces the old
      // /trucks/internal-external fetch that pulled the whole fleet into the browser.
      this.loadCategoryShellsFromJobFile();
      return;
    }
    this.dynamicFormService
      .fetchValuesForDropdown(
        this.config.dataSourceUrl,
        this.config.dataSourceUrlSuffix,
        false,
        {},
        { operationId: id ? id : this.jobFileData.operationId }
      )
      .pipe(
        takeUntil(this.unsubscribe)
      )
      .subscribe(
        (data: any) => {
          const newInternalCategories = {};
          const newInternalCategoryList = {};
          const newExternalCategories = {};
          const newExternalCategoryList = {};

          const newStandardCategories = this.createDeepCopy(this.standardVehicleCategories || {});
          const newStandardCategoryList = this.createDeepCopy(this.vehicleCategoryListStandard || {});

          data.mainOrgVehicles.forEach((vcld) => {
            const vehiclesWithSelection = vcld.vehiclesData.map(vehicle => ({
              ...vehicle,
              isSelected: vehicle.isSelected !== undefined ? vehicle.isSelected : true
            }));

            newInternalCategories[vcld.vehicleCategory] = {
              count: vcld.vehiclesData.length,
              vehicleData: vehiclesWithSelection,
            };
            newInternalCategoryList[vcld.vehicleCategory] = {
              count: vcld.vehiclesData.length,
              vehicleData: vehiclesWithSelection,
            };
          });

          data.orgPatrnerVehicles.forEach((vcld) => {
            const vehiclesWithSelection = vcld.vehiclesData.map(vehicle => ({
              ...vehicle,
              isSelected: vehicle.isSelected !== undefined ? vehicle.isSelected : true
            }));

            newExternalCategories[vcld.vehicleCategory] = {
              count: vcld.vehiclesData.length,
              vehicleData: vehiclesWithSelection,
            };
            newExternalCategoryList[vcld.vehicleCategory] = {
              count: vcld.vehiclesData.length,
              vehicleData: vehiclesWithSelection,
            };
          });

          for (const category in newStandardCategories) {
            if (newStandardCategories.hasOwnProperty(category)) {
              const updatedCategory = this.createDeepCopy(newStandardCategories[category]);
              updatedCategory.vehicleData = updatedCategory.vehicleData.map(vehicle => ({
                ...vehicle,
                isSelected: vehicle.isSelected !== undefined ? vehicle.isSelected : true
              }));
              newStandardCategories[category] = updatedCategory;
              newStandardCategoryList[category] = this.createDeepCopy(updatedCategory);
            }
          }

          this.internalVehicleCategories = newInternalCategories;
          this.vehicleCategoryListInternal = newInternalCategoryList;
          this.externalVehicleCategories = newExternalCategories;
          this.vehicleCategoryListExternal = newExternalCategoryList;

          this.standardVehicleCategories = newStandardCategories;
          this.vehicleCategoryListStandard = newStandardCategoryList;

          this.store$.dispatch(
            new setOptimizationInfo({
              internalVehicleCategories: this.internalVehicleCategories,
              externalVehicleCategories: this.externalVehicleCategories,
              standardVehicleCategories: this.standardVehicleCategories,
              vehicleCategoryListInternal: this.vehicleCategoryListInternal,
              vehicleCategoryListExternal: this.vehicleCategoryListExternal,
              vehicleCategoryListStandard: this.vehicleCategoryListStandard,
              globalCapacityUtilization: this.globalCapacityUtilization ?? 100,
              isIndividualUtilizationSet: false
            })
          );
          this.generateVehicleCategoryFormControls({
            ...this.internalVehicleCategories,
            ...this.externalVehicleCategories,
            ...this.standardVehicleCategories
          });
          this.processAlreadySavedData();
        },
        (err) => {
          console.log('Error loading vehicle length categories: ', err);
        }
      );
  }

  editVehicleCategory(vehicleType: string, vehicleCount: number, vehicleCategory: number) {
    if (this.isOptimizing) {
      return;
    }
    if (vehicleType === 'STANDARD') {
      const dialogRef = this.dialogHandlerService.openDialog(
        VehicleCountModifierComponent,
        {
          isEditing: true,
          vehicleType: `${vehicleCategory}`,
          count: vehicleCount,
          showVehicleDetails: true,
          vehicleDetails: {
            ...this.standardVehicleCategories[vehicleCategory]?.vehicleData[0],
            capacityUtilization: this.standardVehicleCategories[vehicleCategory]?.vehicleData[0]?.capacityUtilization ?? 100
          }
        },
        {
          width: '1000px',
          height: 'auto',
          disableClose: true,
        },
        DialogHandlerDefaultDialogCustomPositionChoice.DISABLED
      );

      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          const chosenUtilization = Number(result.vehicleDetails?.capacityUtilization
            ?? this.standardVehicleCategories[vehicleCategory]?.vehicleData[0]?.capacityUtilization
            ?? this.globalCapacityUtilization);
          const originalVehicleData: VehicleData = this.standardVehicleCategories[vehicleCategory]?.vehicleData[0] || {
            maxLoadVolume: 15,
            maxLoadWeight: 15000,
            dimensions: {
              length: vehicleCategory,
              width: 0,
              height: 0
            },
            vehicleNumber: 'Standard',
            costPerKM: 0,
            capacityUtilization: 100,
            id: '',
            isUpdated: false
          };

          const vehicleData = Array(result.count).fill(null).map(() => ({
            ...JSON.parse(JSON.stringify(originalVehicleData)),
            ...result.vehicleDetails,
            dimensions: {
              length: originalVehicleData.dimensions.length || 0,
              width: originalVehicleData.dimensions.width || 0,
              height: originalVehicleData.dimensions.height || 0
            },
            costPerKM: result.vehicleDetails.costPerKM || originalVehicleData.costPerKM || 0,
            capacityUtilization: chosenUtilization,
            vehicleNumber: originalVehicleData.vehicleNumber || 0,
            maxLoadVolume: result.vehicleDetails.maxLoadVolume || originalVehicleData.maxLoadVolume || 15,
            maxLoadWeight: result.vehicleDetails.maxLoadWeight || originalVehicleData.maxLoadWeight || 15000,
            // Only an utilization that differs from the global counts as individually edited. Flagging
            // every saved vehicle made the global slider warn on every move and, because the global is
            // inferred from the first NON-edited vehicle, left nothing to infer it from.
            isUpdated: chosenUtilization !== Number(this.globalCapacityUtilization),
            isSelected: true
          }));

          const updatedCategories = this.createDeepCopy(this.standardVehicleCategories || {});
          updatedCategories[vehicleCategory] = {
            count: result.count,
            vehicleData
          };
          this.standardVehicleCategories = updatedCategories;

          this.generateVehicleCategoryFormControls(this.standardVehicleCategories);
          this.saveToDatabase();
          // Persist immediately: the backend answers "is anything individually modified?" before the
          // global slider applies, and it can only count what the jobfile holds.
          this.persistStandardVehicleCapacitiesToJobFile();
        }
      });
    } else {
      let vehicleData: VehicleData[] = [];

      if (vehicleType === 'INTERNAL') {
        vehicleData = this.internalVehicleCategories[vehicleCategory]?.vehicleData?.slice() || [];
      } else if (vehicleType === 'EXTERNAL') {
        vehicleData = this.externalVehicleCategories[vehicleCategory]?.vehicleData?.slice() || [];
      }

      const dialogRef = this.dialogHandlerService.openDialog(
        EditDigitalTwinComponent,
        {
          vehicleType,
          vehicleCategory: `${vehicleCategory}`,
          vehicles: vehicleData,
          // Page the rows from the JOBFILE instead of passing the whole category in memory
          // (same model as wizard-v2). Falls back to the legacy in-memory list if ids are missing.
          serverPaged: USE_PAGINATED_VEHICLE_SELECTOR
            && !!this.jobFileData?.orgId && !!this.jobFileData?.jobRefId,
          orgId: this.jobFileData?.orgId,
          jobRefId: this.jobFileData?.jobRefId,
          // Prefer the real category id from the summary; fall back to v1's key (the backend also
          // matches loosely on name/key, e.g. "40FT").
          categoryId: this.categoryIdByName.get(`${vehicleCategory}`) || `${vehicleCategory}`,
          // This screen has one card per ownership, and the same category can exist in BOTH buckets, so
          // the categoryId alone is ambiguous — page and select only the bucket that was clicked.
          ownership: vehicleType
        },
        {
          width: '1100px',
          height: 'auto',
          disableClose: true,
        },
        DialogHandlerDefaultDialogCustomPositionChoice.DISABLED
      );

      dialogRef.afterClosed().subscribe(result => {
        if (result && result.serverPaged) {
          // Rows were persisted straight to the jobfile by the dialog; refresh the category counts
          // from the jobfile instead of rebuilding from an in-memory list we no longer hold.
          if (result.saved) {
            this.refreshCategoryCountsFromJobFile();
          }
          return;
        }
        if (result) {
          const vehicles = result.vehicles || result;
          const hasCustomUtilization = result.hasCustomUtilization || false;

          if (vehicleType === 'INTERNAL') {
            const updatedInternalCategories = this.createDeepCopy(this.internalVehicleCategories);

            updatedInternalCategories[vehicleCategory] = {
              ...updatedInternalCategories[vehicleCategory],
              vehicleData: vehicles.map(vehicle => ({
                ...vehicle,
                isUpdated: vehicle.isUpdated === true,
                isSelected: vehicle.isSelected !== undefined ? vehicle.isSelected : true
              }))
            };

            // Replace with the updated copy
            this.internalVehicleCategories = updatedInternalCategories;
          } else if (vehicleType === 'EXTERNAL') {
            const updatedExternalCategories = this.createDeepCopy(this.externalVehicleCategories);

            updatedExternalCategories[vehicleCategory] = {
              ...updatedExternalCategories[vehicleCategory],
              vehicleData: vehicles.map(vehicle => ({
                ...vehicle,
                isUpdated: vehicle.isUpdated === true,
                isSelected: vehicle.isSelected !== undefined ? vehicle.isSelected : true
              }))
            };

            this.externalVehicleCategories = updatedExternalCategories;
          }

          this.generateVehicleCategoryFormControls(
            vehicleType === 'INTERNAL' ? this.internalVehicleCategories : this.externalVehicleCategories
          );
          this.saveToDatabase();
          // Write the change through to the jobfile right away (Update button).
          this.persistVehicleCapacitiesToJobFile();
        }
      });
    }
  }

  private generateVehicleData(
    categories: EntityMap<
      number,
      { count: number; vehicleData: VehicleData[] }
    >,
    vehicleCategory: number,
    modifiedCount: number,
    originalCategory: EntityMap<
      number,
      { count: number; vehicleData: VehicleData[] }
    >
  ) {
    const categoriesCopy = JSON.parse(JSON.stringify(categories));

    let vehicleData = [...categoriesCopy[vehicleCategory].vehicleData];
    const currentCount = vehicleData.length;
    const countDiff = modifiedCount - currentCount;

    if (countDiff > 0) {
      const availableVehicles = originalCategory[vehicleCategory]?.vehicleData || [];
      const unusedVehicles = availableVehicles.filter(v => !vehicleData.some(vd => vd.vehicleNumber === v.vehicleNumber));
      for (let i = 0; i < countDiff && i < unusedVehicles.length; i++) {
        vehicleData.push({ ...unusedVehicles[i] });
      }
      if (vehicleData.length < modifiedCount && availableVehicles.length > 0) {
        const lastAvailableVehicle = availableVehicles[availableVehicles.length - 1];
        while (vehicleData.length < modifiedCount) {
          vehicleData.push({ ...lastAvailableVehicle });
        }
      }
    } else if (countDiff < 0) {
      vehicleData = vehicleData.slice(0, modifiedCount);
    }

    categoriesCopy[vehicleCategory] = {
      ...categoriesCopy[vehicleCategory],
      count: vehicleData.length,
      vehicleData,
    };

    return categoriesCopy;
  }

  private generateVehicleCategoryFormControls(categories): void {
    if (!this.group) {
      return;
    }

    const vehicleCapacities = this.group.get('vehicleCapacities') as FormGroup;
    if (vehicleCapacities) {
      this.group.removeControl('vehicleCapacities');
    }

    const internalGroup: FormGroup = new FormGroup({});
    const externalGroup: FormGroup = new FormGroup({});
    const standardGroup: FormGroup = new FormGroup({});
    Object.keys(this.internalVehicleCategories).forEach((VehicleCategory) => {
      internalGroup.addControl(
        VehicleCategory,
        new FormGroup({
          count: new FormControl(
            this.internalVehicleCategories[VehicleCategory].count
          ),
          vehicleCategory: new FormControl(+VehicleCategory),
          vehiclesData: new FormArray(
            this.internalVehicleCategories[VehicleCategory].vehicleData.map(
              (vd: VehicleData) => {
                return new FormGroup({
                  maxLoadVolume: new FormControl(vd?.maxLoadVolume),
                  maxLoadWeight: new FormControl(vd?.maxLoadWeight),
                  dimensions: new FormGroup({
                    length: new FormControl(vd?.dimensions?.length),
                    width: new FormControl(vd?.dimensions?.width),
                    height: new FormControl(vd?.dimensions?.height),
                  }),
                  vehicleNumber: new FormControl(vd?.vehicleNumber || 0),
                  costPerKM: new FormControl(vd?.costPerKM || 0),
                  capacityUtilization: new FormControl(vd?.capacityUtilization !== undefined ? vd.capacityUtilization : 100),
                  isSelected: new FormControl(vd?.isSelected !== undefined ? vd.isSelected : true),
                  isUpdated: new FormControl(vd?.isUpdated !== undefined ? vd.isUpdated : false),
                  id: new FormControl(vd?.id)
                });
              }
            )
          ),
        })
      );
    });
    Object.keys(this.externalVehicleCategories).forEach((VehicleCategory) => {
      externalGroup.addControl(
        VehicleCategory,
        new FormGroup({
          count: new FormControl(
            this.externalVehicleCategories[VehicleCategory].count
          ),
          vehicleCategory: new FormControl(+VehicleCategory),
          vehiclesData: new FormArray(
            this.externalVehicleCategories[VehicleCategory].vehicleData.map(
              (vd: VehicleData) => {
                return new FormGroup({
                  maxLoadVolume: new FormControl(vd?.maxLoadVolume),
                  maxLoadWeight: new FormControl(vd?.maxLoadWeight),
                  dimensions: new FormGroup({
                    length: new FormControl(vd?.dimensions?.length),
                    width: new FormControl(vd?.dimensions?.width),
                    height: new FormControl(vd?.dimensions?.height),
                  }),
                  vehicleNumber: new FormControl(vd?.vehicleNumber || 0),
                  costPerKM: new FormControl(vd?.costPerKM || 0),
                  capacityUtilization: new FormControl(vd?.capacityUtilization !== undefined ? vd.capacityUtilization : 100),
                  isSelected: new FormControl(vd?.isSelected !== undefined ? vd.isSelected : true),
                  isUpdated: new FormControl(vd?.isUpdated !== undefined ? vd.isUpdated : false),
                  id: new FormControl(vd?.id)
                });
              }
            )
          ),
        })
      );
    });
    Object.keys(this.standardVehicleCategories).forEach((VehicleCategory) => {
      standardGroup.addControl(
        VehicleCategory,
        new FormGroup({
          count: new FormControl(
            this.standardVehicleCategories[VehicleCategory].count
          ),
          vehicleCategory: new FormControl(+VehicleCategory),
          vehiclesData: new FormArray(
            this.standardVehicleCategories[VehicleCategory].vehicleData.map(
              (vd: VehicleData) => {
                return new FormGroup({
                  maxLoadVolume: new FormControl(vd?.maxLoadVolume),
                  maxLoadWeight: new FormControl(vd?.maxLoadWeight),
                  dimensions: new FormGroup({
                    length: new FormControl(vd?.dimensions?.length),
                    width: new FormControl(vd?.dimensions?.width),
                    height: new FormControl(vd?.dimensions?.height),
                  }),
                  vehicleNumber: new FormControl(vd?.vehicleNumber || 0),
                  costPerKM: new FormControl(vd?.costPerKM || 0),
                  capacityUtilization: new FormControl(
                    vd?.capacityUtilization !== undefined && vd?.capacityUtilization !== null ?
                    vd.capacityUtilization :
                    100
                  ),
                  isSelected: new FormControl(vd?.isSelected !== undefined ? vd.isSelected : true),
                  isUpdated: new FormControl(vd?.isUpdated !== undefined ? vd.isUpdated : false),
                  id: new FormControl(vd?.id)
                });
              }
            )
          ),
        })
      );
    });
    this.group.addControl(
      'vehicleCapacities',
      new FormGroup({
        internal: internalGroup,
        external: externalGroup,
        standard: standardGroup,
      })
    );
  }

  private patchFormData(
    category: { count: number; vehicleData: VehicleData[] },
    vehicleType: string,
    VehicleCategoryType: number
  ): void {
    if (!vehicleType) {
      return;
    }
    category.vehicleData = category.vehicleData.slice(0, category.count);

    const controller = (
      this.group
        .get('vehicleCapacities')
        .get(vehicleType.toLowerCase()) as FormGroup
    ).controls['' + VehicleCategoryType];

    controller.patchValue({
      count: category.count,
      vehicleCategory: +VehicleCategoryType,
    });
    const vehiclesDataFormArray: FormArray = (
      this.group
        .get('vehicleCapacities')
        .get(vehicleType.toLowerCase()) as FormGroup
    ).controls['' + VehicleCategoryType].get('vehiclesData') as FormArray;

    vehiclesDataFormArray.clear();
    category.vehicleData
      .filter((category) => category)
      .map((vd: VehicleData) => {
        return new FormGroup({
          maxLoadVolume: new FormControl(vd?.maxLoadVolume || 15),
          maxLoadWeight: new FormControl(vd?.maxLoadWeight || 15000),
          dimensions: new FormGroup({
            length: new FormControl(vd?.dimensions?.length || 0),
            width: new FormControl(vd?.dimensions?.width || 0),
            height: new FormControl(vd?.dimensions?.height || 0),
          }),
          vehicleNumber: new FormControl(vd?.vehicleNumber || 0),
          costPerKM: new FormControl(vd?.costPerKM || 0),
          capacityUtilization: new FormControl(vd?.capacityUtilization !== undefined ? vd.capacityUtilization : 100),
          isSelected: new FormControl(vd?.isSelected !== undefined ? vd.isSelected : true),
          isUpdated: new FormControl(vd?.isUpdated !== undefined ? vd.isUpdated : false),
          id: new FormControl(vd?.id)
        });
      })
      .forEach((cntrl) => vehiclesDataFormArray.push(cntrl));
  }

  setExternalType(type: number): void {
    if (this.isOptimizing) {
      return;
    }
    const VehicleData: VehicleData[] =
      this.internalVehicleCategories[type]?.vehicleData ?? [];
    if (!this.externalVehicleCategories[type]) {
      const updatedCategories = this.createDeepCopy(this.externalVehicleCategories);

      updatedCategories[type] = {
        count: 1,
        vehicleData: [
          {
            maxLoadVolume: VehicleData
              ? VehicleData[VehicleData.length - 1].maxLoadVolume
              : 15,
            maxLoadWeight: VehicleData
              ? VehicleData[VehicleData.length - 1].maxLoadWeight
              : 15000,
            dimensions: {
              length: VehicleData
                ? VehicleData[VehicleData.length - 1].dimensions.length
                : 0,
              width: VehicleData
                ? VehicleData[VehicleData.length - 1].dimensions.width
                : 0,
              height: VehicleData
                ? VehicleData[VehicleData.length - 1].dimensions.height
                : 0,
            },
            vehicleNumber: VehicleData
              ? VehicleData[VehicleData.length - 1].vehicleNumber
              : 0,
            costPerKM: VehicleData
              ? VehicleData[VehicleData.length - 1].costPerKM
              : 0,
            capacityUtilization: 100,
            id: VehicleData
              ? VehicleData[VehicleData.length - 1].id
              : ''
          },
        ],
      };

      this.externalVehicleCategories = updatedCategories;
    }
    this.addExternalCapacities(type);
  }

  addExternalCapacities(VehicleCategory) {
    const externalGroup: FormGroup = new FormGroup({});
    externalGroup.addControl(
      VehicleCategory,
      new FormGroup({
        count: new FormControl(
          this.externalVehicleCategories[VehicleCategory].count
        ),
        vehicleCategory: new FormControl(+VehicleCategory),
        vehiclesData: new FormArray(
          this.externalVehicleCategories[VehicleCategory].vehicleData.map(
            (vd: VehicleData) => {
              return new FormGroup({
                maxLoadVolume: new FormControl(vd.maxLoadVolume),
                maxLoadWeight: new FormControl(vd.maxLoadWeight),
                capacityUtilization: new FormControl(vd.capacityUtilization !== undefined ? vd.capacityUtilization : 100)
              });
            }
          )
        ),
      })
    );
    const externalGp = this.group
      .get('vehicleCapacities')
      .get('external') as FormGroup;
    externalGp.addControl(
      VehicleCategory,
      new FormGroup({
        count: new FormControl(
          this.externalVehicleCategories[VehicleCategory].count
        ),
        vehicleCategory: new FormControl(+VehicleCategory),
        vehiclesData: new FormArray(
          this.externalVehicleCategories[VehicleCategory].vehicleData.map(
            (vd: VehicleData) => {
              return new FormGroup({
                maxLoadVolume: new FormControl(vd.maxLoadVolume),
                maxLoadWeight: new FormControl(vd.maxLoadWeight),
                capacityUtilization: new FormControl(vd.capacityUtilization !== undefined ? vd.capacityUtilization : 100)
              });
            }
          )
        ),
      })
    );
  }

  openAddStandardVehicle(): void {
    if (this.isOptimizing) {
      return;
    }
    const dialogRef = this.dialogHandlerService.openDialog(
      VehicleCountModifierComponent,
      {
        title: 'Add Standard Vehicle',
        showVehicleDetails: true,
        dataSourceUrl: this.config?.dataSourceUrl || 'api/vehicle-categories',
        dataSourceUrlSuffix: this.config?.dataSourceUrlSuffix || '',
        operationId: this.jobFileData?.operationId,
        vehicleDetails: {
          maxLoadVolume: 15,
          maxLoadWeight: 15000,
          costPerKm: 0,
          // Start at the current global (the dialog otherwise defaults to 100), so adding a vehicle
          // while the global is, say, 80 does not silently create an 80-vs-100 mismatch.
          capacityUtilization: this.globalCapacityUtilization,
          dimensions: {
            length: 0,
            width: 0,
            height: 0
          }
        } as VehicleType
      },
      {
        width: '1000px',
        height: 'auto',
        disableClose: true,
        // position: { top: '50vh', left: '50vw' },
        // panelClass: 'xx',
      },
      DialogHandlerDefaultDialogCustomPositionChoice.DISABLED
    );

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        const vehicleType = result.vehicleType;
        const chosenUtilization = Number(result.vehicleDetails?.capacityUtilization ?? this.globalCapacityUtilization);
        const vehicleData = Array(result.count).fill(null).map(() => ({
          maxLoadVolume: result.vehicleDetails.maxLoadVolume,
          maxLoadWeight: result.vehicleDetails.maxLoadWeight,
          dimensions: {
            length: result.vehicleDetails.dimensions.length,
            width: result.vehicleDetails.dimensions.width,
            height: result.vehicleDetails.dimensions.height
          },
          id: null,
          vehicleNumber: 'Standard',
          category: vehicleType,
          costPerKM: result.vehicleDetails.costPerKM,
          capacityUtilization: chosenUtilization,
          isSelected: true,
          // Individually edited only if the user moved it off the global (see the edit path).
          isUpdated: chosenUtilization !== Number(this.globalCapacityUtilization)
        }));

        const updatedCategories = JSON.parse(JSON.stringify(this.standardVehicleCategories || {}));

        if (!updatedCategories[vehicleType]) {
          updatedCategories[vehicleType] = {
            count: result.count,
            vehicleData
          };
        } else {
          updatedCategories[vehicleType].count += result.count;
          updatedCategories[vehicleType].vehicleData = [
            ...updatedCategories[vehicleType].vehicleData,
            ...vehicleData
          ];
        }

        this.standardVehicleCategories = updatedCategories;

        this.generateVehicleCategoryFormControls(this.standardVehicleCategories);
        this.saveToDatabase();
        // Persist immediately so the backend's utilization-impact check can see these vehicles.
        this.persistStandardVehicleCapacitiesToJobFile();
      }
    });
  }

  updateCapacityUtilization(vehicleCategory: number, value: number): void {
    if (this.standardVehicleCategories[vehicleCategory]) {
      const updatedCategories = this.createDeepCopy(this.standardVehicleCategories);

      updatedCategories[vehicleCategory].vehicleData =
        updatedCategories[vehicleCategory].vehicleData.map(vehicle => ({
          ...vehicle,
          capacityUtilization: value,
          isUpdated: true,
          isSelected: true
        }));

      this.standardVehicleCategories = updatedCategories;

      const standardGroup = this.group.get('vehicleCapacities').get('standard') as FormGroup;
      if (standardGroup && standardGroup.controls[vehicleCategory.toString()]) {
        const vehicleCategoryControl = standardGroup.controls[vehicleCategory.toString()] as FormGroup;
        const vehiclesDataFormArray = vehicleCategoryControl.get('vehiclesData') as FormArray;

        if (vehiclesDataFormArray) {
          for (let i = 0; i < vehiclesDataFormArray.length; i++) {
            const vehicleFormGroup = vehiclesDataFormArray.at(i) as FormGroup;
            vehicleFormGroup.get('capacityUtilization').setValue(value, { emitEvent: false });

            if (!vehicleFormGroup.get('isUpdated')) {
              vehicleFormGroup.addControl('isUpdated', new FormControl(true));
            } else {
              vehicleFormGroup.get('isUpdated').setValue(true, { emitEvent: false });
            }
          }
        }
      }

      this.saveToDatabase(true);
      // An explicit per-category utilization must reach the jobfile too, or the impact check will not
      // know it exists and the next global change will overwrite it unasked.
      this.persistStandardVehicleCapacitiesToJobFile();
    }
  }

  updateGlobalCapacityUtilization(value: number): void {
    if (this.isOptimizing) {
      return;
    }
    value = Math.max(0, Math.min(100, value));
    const originalValue = this.globalCapacityUtilization;
    this.globalCapacityUtilization = value;
    this.group.get('globalCapacityUtilization').setValue(value);

    if (this.usePaginatedSelector) {
      // The in-memory loops below can only reach the standard vehicles — internal/external hold no rows
      // here, so the slider used to change nothing for the digital twin. Route through the debounced
      // pipeline instead: ask the backend what would be overwritten, prompt if needed, then write the
      // new global to the jobfile (same shape as wizard-v2's intent re-save).
      this.globalUtilInput$.next(value);
      return;
    }

    this.optimizationInfo$.pipe(first()).subscribe(info => {
      const isIndividualSet = info?.isIndividualUtilizationSet === true;
      if (!isIndividualSet) {
        this.applyGlobalCapacityUtilizationToAll(value);
      } else {
        if (this.isCapacityWarningDialogOpen) {
          this.globalCapacityUtilization = value;
          this.group.get('globalCapacityUtilization').setValue(originalValue, { emitEvent: false });
          return;
        }
        this.isCapacityWarningDialogOpen = true;
        const dialogRef = this.dialogHandlerService.openDialog(
          CapacityWarningDialogComponent,
          {
            message: 'You have Previously Modified the Capacity Utilisation for One or More Vehicles',
            leftButtonText: 'Apply to Non Modified Vehicles Only',
            rightButtonText: 'Apply to all vehicles'
          },
          {
            width: '800px',
            height: null,
            disableClose: true,
            panelClass: 'capacity-warning-dialog-panel',
            maxWidth: '95vw',
            maxHeight: null,
            position: null,
            hasBackdrop: true,
            autoFocus: false
          }
        );
        dialogRef.afterClosed().pipe(take(1)).subscribe(result => {
          this.isCapacityWarningDialogOpen = false;
          if (result === 'all') {
            this.applyGlobalCapacityUtilizationToAll(this.globalCapacityUtilization);
          } else if (result === 'nonModified') {
            this.applyGlobalCapacityUtilizationToNonModified(this.globalCapacityUtilization);
          } else {
            this.globalCapacityUtilization = originalValue;
            this.group.get('globalCapacityUtilization').setValue(originalValue, { emitEvent: false });
          }
        });
      }
    });
  }

  /**
   * Paginated mode: only the backend knows whether any vehicle carries an individually-edited
   * utilization (the browser holds no rows), so ask first and prompt only if there is something to
   * overwrite. Same decision flow wizard-v2 uses before re-saving its intent.
   */
  private decideGlobalUtilizationApplyMode(value: number): void {
    if (this.isOptimizing || this.isCapacityWarningDialogOpen) {
      return;
    }
    const orgId = this.jobFileData?.orgId;
    const jobRefId = this.jobFileData?.jobRefId;
    if (!orgId || !jobRefId) {
      return;
    }

    this.jobFileService.getUtilizationImpact(orgId, jobRefId)
      .pipe(take(1), takeUntil(this.unsubscribe))
      .subscribe({
        next: (impact: any) => {
          if (!impact || !impact.modifiedVehicles) {
            // Nothing individually edited — the new global simply applies everywhere.
            this.commitGlobalUtilization(value, 'ALL');
            return;
          }
          this.promptGlobalUtilizationApplyMode(value);
        },
        // Without the impact answer, take the non-destructive option rather than silently overwriting.
        error: (err) => {
          console.error('Failed to read utilization impact', err);
          this.commitGlobalUtilization(value, 'NON_MODIFIED');
        }
      });
  }

  private promptGlobalUtilizationApplyMode(value: number): void {
    const previousValue = this.lastCommittedGlobalUtilization;
    this.isCapacityWarningDialogOpen = true;

    const dialogRef = this.dialogHandlerService.openDialog(
      CapacityWarningDialogComponent,
      {
        message: 'You have Previously Modified the Capacity Utilisation for One or More Vehicles',
        leftButtonText: 'Apply to Non Modified Vehicles Only',
        rightButtonText: 'Apply to all vehicles'
      },
      {
        width: '800px',
        height: null,
        disableClose: true,
        panelClass: 'capacity-warning-dialog-panel',
        maxWidth: '95vw',
        maxHeight: null,
        position: null,
        hasBackdrop: true,
        autoFocus: false
      }
    );

    dialogRef.afterClosed().pipe(take(1)).subscribe(result => {
      this.isCapacityWarningDialogOpen = false;
      if (result === 'all') {
        this.commitGlobalUtilization(value, 'ALL');
      } else if (result === 'nonModified') {
        this.commitGlobalUtilization(value, 'NON_MODIFIED');
      } else {
        this.globalCapacityUtilization = previousValue;
        this.group.get('globalCapacityUtilization').setValue(previousValue, { emitEvent: false });
      }
    });
  }

  /**
   * Record the new global utilization: the in-memory pass still covers the standard vehicles (they live
   * only in the browser and are sent with the optimize payload), and the jobfile write is what reaches
   * the digital-twin vehicles, which the browser no longer holds.
   */
  private commitGlobalUtilization(value: number, applyMode: 'ALL' | 'NON_MODIFIED'): void {
    this.lastCommittedGlobalUtilization = value;

    if (applyMode === 'ALL') {
      this.applyGlobalCapacityUtilizationToAll(value);
    } else {
      this.applyGlobalCapacityUtilizationToNonModified(value);
    }

    const orgId = this.jobFileData?.orgId;
    const jobRefId = this.jobFileData?.jobRefId;
    if (!orgId || !jobRefId) {
      return;
    }
    this.jobFileService.applyGlobalUtilization(orgId, jobRefId, value, applyMode)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        error: (err) => console.error('Failed to apply global capacity utilization to jobfile', err)
      });
  }

  getSelectedVehicleCount(vehicleType: string, categoryKey: any): number {
    let categoryData: any;
    if (vehicleType === 'STANDARD') {
      categoryData = this.standardVehicleCategories[categoryKey];
      // For standard vehicles, isSelected should always be true, so return total count
      if (!categoryData || !categoryData.vehicleData || !categoryData.vehicleData.length) {
        return 0;
      }
      return categoryData.vehicleData.length;
    } else if (vehicleType === 'INTERNAL') {
      categoryData = this.internalVehicleCategories[categoryKey];
    } else if (vehicleType === 'EXTERNAL') {
      categoryData = this.externalVehicleCategories[categoryKey];
    }
    if (!categoryData) {
      return 0;
    }
    // Paginated mode: no vehicle rows are held in the browser, so the count comes from the
    // server-provided category counts (`count` = selected, `totalCount` = all). Counting
    // `vehicleData` here would always be 0.
    if (!categoryData.vehicleData || !categoryData.vehicleData.length) {
      return categoryData.count != null ? categoryData.count
        : (categoryData.totalCount != null ? categoryData.totalCount : 0);
    }
    return categoryData.vehicleData.filter(vehicle => vehicle.isSelected === true).length;
  }

  private applyGlobalCapacityUtilizationToNonModified(value: number): void {
    this.globalCapacityUtilization = value;
    this.group.get('globalCapacityUtilization').setValue(value);

    for (const category in this.standardVehicleCategories) {
      if (this.standardVehicleCategories.hasOwnProperty(category)) {
        const updatedCategories = this.createDeepCopy(this.standardVehicleCategories);
        updatedCategories[category].vehicleData = updatedCategories[category].vehicleData.map(vehicle => {
          if (!vehicle.isUpdated) {
            return {
              ...vehicle,
              capacityUtilization: value,
              isUpdated: false,
              isSelected: true
            };
          }
          return {
            ...vehicle,
            isSelected: true
          };
        });
        this.standardVehicleCategories = updatedCategories;
      }
    }

    for (const category in this.internalVehicleCategories) {
      if (this.internalVehicleCategories.hasOwnProperty(category)) {
        const updatedCategories = this.createDeepCopy(this.internalVehicleCategories);
        updatedCategories[category].vehicleData = updatedCategories[category].vehicleData.map(vehicle => {
          if (!vehicle.isUpdated) {
            return {
              ...vehicle,
              capacityUtilization: value,
              isUpdated: false,
              isSelected: vehicle.isSelected !== undefined ? vehicle.isSelected : true
            };
          }
          return vehicle;
        });
        this.internalVehicleCategories = updatedCategories;
      }
    }

    for (const category in this.externalVehicleCategories) {
      if (this.externalVehicleCategories.hasOwnProperty(category)) {
        const updatedCategories = this.createDeepCopy(this.externalVehicleCategories);
        updatedCategories[category].vehicleData = updatedCategories[category].vehicleData.map(vehicle => {
          if (!vehicle.isUpdated) {
            return {
              ...vehicle,
              capacityUtilization: value,
              isUpdated: false,
              isSelected: vehicle.isSelected !== undefined ? vehicle.isSelected : true
            };
          }
          return vehicle;
        });
        this.externalVehicleCategories = updatedCategories;
      }
    }

    this.generateVehicleCategoryFormControls({
      ...this.internalVehicleCategories,
      ...this.externalVehicleCategories,
      ...this.standardVehicleCategories
    });

    this.saveToDatabase(true);
  }

  private applyGlobalCapacityUtilizationToAll(value: number): void {
    this.globalCapacityUtilization = value;
    this.group.get('globalCapacityUtilization').setValue(value);

    for (const category in this.standardVehicleCategories) {
      if (this.standardVehicleCategories.hasOwnProperty(category)) {
        const updatedCategories = this.createDeepCopy(this.standardVehicleCategories);
        updatedCategories[category].vehicleData = updatedCategories[category].vehicleData.map(vehicle => ({
          ...vehicle,
          capacityUtilization: value,
          isUpdated: false,
          isSelected: true
        }));
        this.standardVehicleCategories = updatedCategories;
      }
    }

    for (const category in this.internalVehicleCategories) {
      if (this.internalVehicleCategories.hasOwnProperty(category)) {
        const updatedCategories = this.createDeepCopy(this.internalVehicleCategories);
        updatedCategories[category].vehicleData = updatedCategories[category].vehicleData.map(vehicle => ({
          ...vehicle,
          capacityUtilization: value,
          isUpdated: false,
          isSelected: vehicle.isSelected !== undefined ? vehicle.isSelected : true
        }));
        this.internalVehicleCategories = updatedCategories;
      }
    }

    for (const category in this.externalVehicleCategories) {
      if (this.externalVehicleCategories.hasOwnProperty(category)) {
        const updatedCategories = this.createDeepCopy(this.externalVehicleCategories);
        updatedCategories[category].vehicleData = updatedCategories[category].vehicleData.map(vehicle => ({
          ...vehicle,
          capacityUtilization: value,
          isUpdated: false,
          isSelected: vehicle.isSelected !== undefined ? vehicle.isSelected : true
        }));
        this.externalVehicleCategories = updatedCategories;
      }
    }

    this.generateVehicleCategoryFormControls({
      ...this.internalVehicleCategories,
      ...this.externalVehicleCategories,
      ...this.standardVehicleCategories
    });

    this.saveToDatabase(false);
  }

  private saveToDatabase(forceIndividualUtilizationSet?: boolean): void {
    let isIndividualUtilizationSet = false;

    if (forceIndividualUtilizationSet !== undefined) {
      isIndividualUtilizationSet = forceIndividualUtilizationSet;
    } else {
      for (const category in this.standardVehicleCategories) {
        if (this.standardVehicleCategories.hasOwnProperty(category)) {
          const vehicleData = this.standardVehicleCategories[category].vehicleData || [];
          for (const vehicle of vehicleData) {
            if (vehicle.isUpdated === true) {
              isIndividualUtilizationSet = true;
              break;
            }
          }
          if (isIndividualUtilizationSet) {
            break;
          }
        }
      }

      if (!isIndividualUtilizationSet) {
        for (const category in this.internalVehicleCategories) {
          if (this.internalVehicleCategories.hasOwnProperty(category)) {
            const vehicleData = this.internalVehicleCategories[category].vehicleData || [];
            for (const vehicle of vehicleData) {
              if (vehicle.isUpdated === true) {
                isIndividualUtilizationSet = true;
                break;
              }
            }
            if (isIndividualUtilizationSet) {
              break;
            }
          }
        }
      }

      if (!isIndividualUtilizationSet) {
        for (const category in this.externalVehicleCategories) {
          if (this.externalVehicleCategories.hasOwnProperty(category)) {
            const vehicleData = this.externalVehicleCategories[category].vehicleData || [];
            for (const vehicle of vehicleData) {
              if (vehicle.isUpdated === true) {
                isIndividualUtilizationSet = true;
                break;
              }
            }
            if (isIndividualUtilizationSet) {
              break;
            }
          }
        }
      }
    }

    if (!this.standardVehicleCategories) {
      this.standardVehicleCategories = {};
    }

    if (!this.vehicleCategoryListStandard) {
      this.vehicleCategoryListStandard = {};
    }

    this.store$.dispatch(
      new setOptimizationInfo({
        internalVehicleCategories: this.internalVehicleCategories,
        externalVehicleCategories: this.externalVehicleCategories,
        standardVehicleCategories: this.standardVehicleCategories,
        vehicleCategoryListInternal: this.vehicleCategoryListInternal,
        vehicleCategoryListExternal: this.vehicleCategoryListExternal,
        vehicleCategoryListStandard: this.vehicleCategoryListStandard,
        globalCapacityUtilization: this.globalCapacityUtilization,
        isIndividualUtilizationSet: isIndividualUtilizationSet,
        jobRefId: this.jobFileData?.jobRefId
      })
    );
  }

  private checkForUpdatedVehicles(info: OptimizationInfo): boolean {
    // Check internal vehicles for updates
    if (info.internalVehicleCategories) {
      for (const category in info.internalVehicleCategories) {
        if (info.internalVehicleCategories.hasOwnProperty(category)) {
          const vehicleData = info.internalVehicleCategories[category].vehicleData || [];
          if (vehicleData.some(vehicle => vehicle.isUpdated === true)) {
            return true;
          }
        }
      }
    }

    // Check external vehicles for updates
    if (info.externalVehicleCategories) {
      for (const category in info.externalVehicleCategories) {
        if (info.externalVehicleCategories.hasOwnProperty(category)) {
          const vehicleData = info.externalVehicleCategories[category].vehicleData || [];
          if (vehicleData.some(vehicle => vehicle.isUpdated === true)) {
            return true;
          }
        }
      }
    }

    // Check standard vehicles for updates
    if (info.standardVehicleCategories) {
      for (const category in info.standardVehicleCategories) {
        if (info.standardVehicleCategories.hasOwnProperty(category)) {
          const vehicleData = info.standardVehicleCategories[category].vehicleData || [];
          if (vehicleData.some(vehicle => vehicle.isUpdated === true)) {
            return true;
          }
        }
      }
    }

    return false;
  }

}
